import React, { FC, useContext, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { UseFormReturn } from 'react-hook-form';
import { HiddenSubmit } from '../HiddenSubmit/HiddenSubmit';
import { TextInputController } from '../InputControllers/TextInputController';
import { CheckboxController } from '../InputControllers/CheckboxController';
import { SelectController } from '../InputControllers/SelectController';
import { ProductToolsFetcher } from '../../fetchers/ProductTools';
import { ProductsFetcher } from '../../fetchers/ProductsFetcher';
import { useAutoFetch } from '../../contexts/Fetcher';
import { TransferListWithAmount } from '../InputControllers/TransferListWithAmount';
import { FormSection } from '../FormSection/FormSection';
import { PackageDurationPricing } from './PackageDurationPricing';
import { PackageDuration } from '../../models/PackageDuration';
import { useScrollToError } from '../../hooks/useScrollToError';
import { useTranslation } from 'react-i18next';

interface IChosenTool {
  quantity: number;
  toolId: string;
}

export interface IProductPackageFormPayload {
  name: string;
  baseDailyPrice: number | string;
  productId: string;
  isWatermark: boolean;
  isAddon: boolean;
  tools: IChosenTool[];
  durations: PackageDuration[];
  customDurationDays: number | string;
  discounts: Partial<Record<PackageDuration, number | string>>;
  perpetualTotalPrice: number | string;
}

interface IProps {
  form: UseFormReturn;
  onSubmit: (payload: IProductPackageFormPayload) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    toolsTransferList: {
      height: 300,
    },
  })
);

export const ProductPackageForm: FC<IProps> = ({ form, onSubmit }) => {
  const { data: toolsData } = useContext(ProductToolsFetcher.Context);
  const { data: productsData } = useContext(ProductsFetcher.Context);
  useAutoFetch(ProductToolsFetcher);
  useAutoFetch(ProductsFetcher);
  const { formRef } = useScrollToError(form);
  const styles = useStyles();
  const { t } = useTranslation();

  const productItems = useMemo(
    () => (productsData?.products ?? []).map((product) => ({ value: product.id, label: product.name })),
    [productsData]
  );

  if (!toolsData || !productsData) return null;
  return (
    <Grid container spacing={3} component="form" onSubmit={form.handleSubmit(onSubmit)} ref={formRef}>
      <Grid item xs={12}>
        <FormSection title={t('adminProducts.packages.form.sections.basicInformation')}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextInputController
                control={form.control}
                errors={form.formState.errors}
                label={t('adminProducts.packages.form.name')}
                name="name"
                rules={{ required: true }}
                InputProps={{
                  placeholder: t('adminProducts.packages.form.namePlaceholder'),
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
      </Grid>

      <Grid item xs={12}>
        <FormSection title={t('adminProducts.packages.form.sections.productAndTools')}>
          {/* Wider gap than the other sections: the transfer list label has no top margin of its own. */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SelectController
                control={form.control}
                errors={form.formState.errors}
                items={productItems}
                label={t('adminProducts.packages.form.product')}
                name="productId"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TransferListWithAmount
                form={form}
                name="tools"
                label={t('adminProducts.packages.form.tools')}
                data={toolsData.tools}
                compare={(item, value: IChosenTool) => item.id === value.toolId}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.name}
                getValueAmount={(value) => value.quantity}
                valueGenerator={(item, quantity) => ({ toolId: item.id, quantity })}
                availableListLabel={t('adminProducts.packages.form.availableTools')}
                chosenListLabel={t('adminProducts.packages.form.chosenTools')}
                classNames={{
                  itemsList: styles.toolsTransferList,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
      </Grid>

      <Grid item xs={12}>
        <FormSection title={t('adminProducts.packages.form.sections.pricingAndDuration')}>
          <PackageDurationPricing form={form} />
        </FormSection>
      </Grid>

      <Grid item xs={12}>
        <FormSection title={t('adminProducts.packages.form.sections.flags')}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <CheckboxController
                control={form.control}
                errors={form.formState.errors}
                label={t('adminProducts.packages.form.watermark.label')}
                name="isWatermark"
              />
              <Typography variant="caption" display="block">
                {t('adminProducts.packages.form.watermark.helper')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <CheckboxController
                control={form.control}
                errors={form.formState.errors}
                label={t('adminProducts.packages.form.isAddon.label')}
                name="isAddon"
              />
              <Typography variant="caption" display="block">
                {t('adminProducts.packages.form.isAddon.helper')}
              </Typography>
            </Grid>
          </Grid>
        </FormSection>
      </Grid>

      <HiddenSubmit />
    </Grid>
  );
};
